import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { SessionResponse } from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../lib/api';

/**
 * Who is signed in, for the whole app.
 *
 * The session itself is an httpOnly cookie the JavaScript cannot read — that is
 * the point of it — so "who am I" is a question only the API can answer. This
 * context asks once on mount and hands the answer down; every screen that needs
 * a role reads it from here rather than re-asking.
 *
 * `status` distinguishes "still asking" from "asked, nobody" on purpose. A
 * component that treats `loading` as `signed out` renders the sign-in page for
 * a flash on every reload, and — worse — an admin screen renders its "you may
 * not be here" state before the answer arrives.
 */
export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

export interface SessionState {
  status: SessionStatus;
  session: SessionResponse | null;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<SessionResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const body = await apiGet<SessionResponse>('/auth/me');
      setSession(body);
      setStatus('authenticated');
    } catch (err) {
      // A 401 is the normal answer for a visitor, not an error worth showing.
      // Anything else (the API is down) is also "not signed in" as far as the
      // shell is concerned; the ready check on the home page reports the API.
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.warn('could not read the session', err);
      }
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await apiSend('POST', '/auth/logout');
    } catch (err) {
      // The local session clears either way, below — signing out is a
      // promise about THIS browser, not about the API call succeeding. A
      // caller that only chained `.then()` off the old version (which
      // rethrew here) left every failure — the network down, a cookie the
      // server had already expired — as an unhandled rejection with the
      // "Sign out" button still on screen and nothing visibly having
      // happened. Logging is enough: there is nothing left for a caller to
      // decide.
      console.warn('could not tell the API about signing out', err);
    } finally {
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      isAdmin: session?.user.role === 'OPERATOR',
      refresh,
      signOut,
    }),
    [status, session, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a <SessionProvider>');
  return value;
}
